import { Point, Rectangle } from 'pixi.js';
import { TextDirection, TextAlignment, DynamicLevel } from '../../../../enums';
import type { TextStyleRenderer } from '../renderer/TextStyleRenderer';
import { isBreakingSpace, isNewline, metricsContext, tokenize } from '../textMetricsHelpers';
import type { Transcript } from '../../../Transcript';
import type { AnimatedValue } from '../renderer/TextDynamicAnimationBase';
import { WordMetrics } from './WordMetrics';
import { WordElementMetrics, getStyleForMetrics } from './WordElementMetrics';
import { TextLineMetrics } from './TextLineMetrics';
import { WordToken } from './WordToken';
import { TextMetricList } from './TextMetricList';
import type { HighlightSpanMetrics } from './HighlightSpanMetrics';
import { equalWithinEpsilon } from '../../utils';

export type MeasurementResult = ReturnType<CanvasRenderingContext2D['measureText']>;

type LargestWordElementsType = {
    ascent: {
        text: string;
        value: number;
    };
    descent: {
        text: string;
        value: number;
    };
};

type CharOffsets = {
    /**
     * index of the line
     */
    lineIndex: number;
    /**
     * index of the word into the selected line
     */
    wordIndex: number;
    /**
     * index of the word element into the selected word
     */
    elementIndex: number;
    /**
     * index of the character into the selected element
     */
    characterIndex: number;
};

/**
 * ROUNDING_PRECISION to RANGE_TOLERANCE relationship:
 * - 0 = 0
 * - 1 = 0.1
 * - 2 = 0.01
 * Both values should always be changed in tandem.
 */
export const ROUNDING_PRECISION: number = 1;

/**
 * Represents the measurement of a block of text. It is broken down in lines, words
 * and word elements.
 */
export class TextMetrics extends TextMetricList<TextLineMetrics> {
    #canDisplayAllText = true;
    #objectToWordElementIndexArray: number[] | null = null;
    #lineIndexToWordElementIndexArray: number[] | null = null;
    #wordIndexToWordElementIndexArray: number[] | null = null;
    #highlightSpanIndexToWordElementIndexArray: number[] | null = null;
    #wordMetricsWithoutBreakingSpaces: WordMetrics[] | null = null;
    #wordMetricsWithBreakingSpaces: WordMetrics[] | null = null;
    #highlightSpans: HighlightSpanMetrics[] | null = null;

    constructor(children: Array<TextLineMetrics>, canDisplayAllText = true) {
        super(children);
        this.#canDisplayAllText = canDisplayAllText;
    }

    override clearCachedValues() {
        super.clearCachedValues();
        this.#objectToWordElementIndexArray = null;
        this.#lineIndexToWordElementIndexArray = null;
        this.#wordIndexToWordElementIndexArray = null;
        this.#highlightSpanIndexToWordElementIndexArray = null;
    }

    get lines(): readonly TextLineMetrics[] {
        return this.children;
    }

    get area(): number {
        return this.width * this.height;
    }

    override _getBounds(): Rectangle {
        if (this.lines.length === 0) {
            return Rectangle.EMPTY;
        }
        const box = this.lines[0].bounds.clone();
        for (let i = 1; i < this.lines.length; i++) {
            const bounds = this.lines[i].bounds;
            box.enlarge(bounds);
        }

        return box;
    }

    /**
     * @return Just the words, excluding breaking spaces.
     */
    get wordMetricsWithoutSpaces(): readonly WordMetrics[] {
        if (!this.#wordMetricsWithoutBreakingSpaces) {
            this.#wordMetricsWithoutBreakingSpaces = this.lines
                .flatMap((line) => line.words)
                .filter((word) => !isBreakingSpace(word.text));
        }
        return this.#wordMetricsWithoutBreakingSpaces;
    }

    /**
     * @return All the word metrics, including breaking spaces.
     */
    get wordMetrics(): readonly WordMetrics[] {
        if (!this.#wordMetricsWithBreakingSpaces) {
            this.#wordMetricsWithBreakingSpaces = this.lines.flatMap((line) => line.words);
        }
        return this.#wordMetricsWithBreakingSpaces;
    }

    get highlightSpans(): readonly HighlightSpanMetrics[] {
        if (!this.#highlightSpans) {
            this.#highlightSpans = this.lines.flatMap((line) => line.highlightSpans);
        }
        return this.#highlightSpans;
    }

    get baseLineOffsetY() {
        return this.style.fontSize * this.style.lineHeight;
    }

    get maxLineHeight() {
        return this.lines.reduce((value, line) => Math.max(value, line.height), 0);
    }

    get maxLineAscent() {
        return this.lines.reduce((value, line) => Math.max(value, line.ascent), 0);
    }

    get maxLineDescent() {
        return this.lines.reduce((value, line) => Math.max(value, line.descent), 0);
    }

    get maxHighlightSpanAscent() {
        return this.highlightSpans.reduce((value, span) => Math.max(value, span.ascent), 0);
    }

    get maxHighlightSpanDescent() {
        return this.highlightSpans.reduce((value, span) => Math.max(value, span.descent), 0);
    }

    get x() {
        return this.bounds.x;
    }

    get y() {
        return this.bounds.y;
    }

    override _getWidth(): number {
        return this.bounds.width;
    }

    override _getHeight(): number {
        return this.bounds.height;
    }

    get canDisplayAllText() {
        return this.#canDisplayAllText;
    }

    override _getSourceWordReferenceIndices(): number[] {
        return this.lines.flatMap((line) => (line as TextLineMetrics).sourceWordReferenceIndices);
    }

    /**
     * Spreads the values over the given level. This is used to shape our input values array to match our target level.
     * For instance, if we want to apply the object level values of [0] to all of our word elements then we need to
     * create an array of the same length as our word elements and fill it with the value [0]. If we have metrics with
     * 3 elements then we need to create an array of length 3 and thus fill it with [0, 0, 0].
     * */
    spreadLevelValues(values: number[] | AnimatedValue[], level: DynamicLevel) {
        const callback = (wordElementIndex: number): number => {
            const entry = values[wordElementIndex] ?? -1;
            if (typeof entry === 'object') {
                return entry.value;
            }
            return entry;
        };
        switch (level) {
            case DynamicLevel.object:
                return this.objectToWordElementIndexArray.map(callback);
            case DynamicLevel.line:
                return this.lineIndexToWordElementIndexArray.map(callback);
            case DynamicLevel.word:
                return this.wordIndexToWordElementIndexArray.map(callback);
            case DynamicLevel.highlight:
                return this.highlightSpanIndexToWordElementIndexArray.map(callback);
            default:
                throw new Error(`Unknown level to spread: ${level}`);
        }
    }

    /** Get a mapping between the object and word elements. */
    get objectToWordElementIndexArray() {
        if (this.#objectToWordElementIndexArray === null) {
            this.#objectToWordElementIndexArray = this._getObjectToWordElementIndexArray();
        }
        return this.#objectToWordElementIndexArray;
    }

    _getObjectToWordElementIndexArray(): number[] {
        const array: number[] = [];
        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                word.elements.forEach(() => {
                    array.push(0);
                });
            });
        });
        return array;
    }

    /** Get a mapping between the line and word elements. */
    get lineIndexToWordElementIndexArray(): readonly number[] {
        if (this.#lineIndexToWordElementIndexArray === null) {
            this.#lineIndexToWordElementIndexArray = this._getLineIndexToWordElementIndexArray();
        }
        return this.#lineIndexToWordElementIndexArray;
    }

    _getLineIndexToWordElementIndexArray() {
        const array: number[] = [];
        this.lines.forEach((line, index) => {
            // we should probably cache the different element counts on each level? Not sure what the performance
            // difference would be.
            line.words.forEach((word) => {
                word.elements.forEach(() => {
                    array.push(index);
                });
            });
        });
        return array;
    }

    /** Get a mapping between the word and word elements. */
    get wordIndexToWordElementIndexArray() {
        if (this.#wordIndexToWordElementIndexArray === null) {
            this.#wordIndexToWordElementIndexArray = this._getWordIndexToWordElementIndexArray();
        }
        return this.#wordIndexToWordElementIndexArray;
    }

    _getWordIndexToWordElementIndexArray() {
        const array: number[] = [];
        let valueIndex = 0;
        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                if (isBreakingSpace(word.text)) {
                    // Map spaces to the preceding word's index so they inherit the same
                    // animation values (e.g. alpha). This ensures underlines and other
                    // decorations render correctly across whitespace during word-level animations.
                    const precedingWordIndex = Math.max(0, valueIndex - 1);
                    word.elements.forEach(() => {
                        array.push(precedingWordIndex);
                    });
                    return;
                }
                word.elements.forEach(() => {
                    array.push(valueIndex);
                });
                valueIndex++;
            });
        });
        return array;
    }

    /**
     * Get the line, word, element and element character index for the provided global
     * character index.
     */
    getOffsetsForChar(charIndex: number): CharOffsets {
        let currIndex = 0;
        // Iterate over all lines
        for (let lineIndex = 0; lineIndex < this.lines.length; lineIndex++) {
            const line = this.lines[lineIndex];

            // Handle if a leading space was removed
            if (line.removedLeadingSpace) {
                if (currIndex === charIndex) {
                    return {
                        lineIndex,
                        wordIndex: 0,
                        elementIndex: 0,
                        characterIndex: 0,
                    };
                }
                currIndex++;
            }

            // Iterate over all words in this line
            for (let wordIndex = 0; wordIndex < line.words.length; wordIndex++) {
                const textWord = line.words[wordIndex];

                // Iterate over all word elements in this line
                for (
                    let elementIndex = 0;
                    elementIndex < textWord.elements.length;
                    elementIndex++
                ) {
                    const wordElement = textWord.elements[elementIndex];

                    // Iterate over all characters in this word element
                    for (
                        let characterIndex = 0;
                        characterIndex < wordElement.text.length;
                        characterIndex++
                    ) {
                        if (currIndex === charIndex) {
                            return {
                                lineIndex,
                                wordIndex,
                                elementIndex,
                                characterIndex,
                            };
                        }
                        currIndex++;
                    }
                }
            }

            // Handle if a trailing space was removed
            if (line.removedTrailingSpace) {
                currIndex++;
                if (currIndex === charIndex) {
                    const wordIndex = line.words.length - 1;
                    const elementIndex = line.words[wordIndex].elements.length - 1;
                    const characterIndex =
                        line.words[wordIndex].elements[elementIndex].text.length - 1;
                    return {
                        lineIndex,
                        wordIndex,
                        elementIndex,
                        characterIndex,
                    };
                }
            }
        }

        // Fallback to after the last character
        let lineIndex = 0;
        let wordIndex = 0;
        let elementIndex = 0;
        let characterIndex = 0;
        if (this.lines.length > 0) {
            lineIndex = this.lines.length - 1;
            const numWordsLastLine = this.lines[lineIndex].words.length;
            if (numWordsLastLine > 0) {
                wordIndex = numWordsLastLine - 1;
                const numElementsLastWord = this.lines[lineIndex].words[wordIndex].elements.length;
                if (numElementsLastWord > 0) {
                    elementIndex = numElementsLastWord - 1;
                    characterIndex =
                        this.lines[lineIndex].words[wordIndex].elements[elementIndex].text.length;
                }
            }
        }

        return {
            lineIndex,
            wordIndex,
            elementIndex,
            characterIndex,
        };
    }

    /**
     * Compute the x-distance into a word element that a provided char index is. Note that this
     * distance is from the word element's origin (and not the bounding box origin).
     *
     * @param lineIndex - the line index in which the char is
     * @param wordIndex - the word index into the line in which the char is
     * @param elementIndex - the word element index into the text word in which the char is
     * @param charIndex - the char index into the word element
     * @returns the location of the char relative to the start of its word element
     */
    positionOfCharRelativeToWordElement(
        lineIndex: number,
        wordIndex: number,
        elementIndex: number,
        charIndex: number,
    ): Point {
        const line = this.lines[lineIndex];
        if (!line) {
            return new Point(0, 0);
        }

        const textWord = line.words[wordIndex];
        if (!textWord) {
            return new Point(0, 0);
        }

        const wordElement = textWord.elements[elementIndex];
        if (!wordElement) {
            return new Point(0, 0);
        }

        return new Point(wordElement.partialAdvanceWidth(0, charIndex), 0);
    }

    /** Get a mapping between the highlight spans and word elements. */
    get highlightSpanIndexToWordElementIndexArray(): readonly number[] {
        if (this.#highlightSpanIndexToWordElementIndexArray === null) {
            this.#highlightSpanIndexToWordElementIndexArray =
                this._getHighlightSpanIndexToWordElementIndexArray();
        }
        return this.#highlightSpanIndexToWordElementIndexArray;
    }

    /**
     * For any words not within a highlight span, it will set a value of -1. For words within a highlight span,
     * it will set a value to the index of the highlight span that those words are a part of.
     */
    _getHighlightSpanIndexToWordElementIndexArray() {
        const array: number[] = [];
        let currentHighlightIndex = -1;
        let isInHighlight = false;
        this.lines.forEach((line) => {
            // highlights do not cross lines
            isInHighlight = false;
            line.words.forEach((word) => {
                word.elements.forEach((element) => {
                    if (element.highlighted) {
                        if (!isInHighlight) {
                            currentHighlightIndex++;
                            isInHighlight = true;
                        }
                        array.push(currentHighlightIndex);
                    } else {
                        array.push(-1);
                        isInHighlight = false;
                    }
                });
            });
        });
        return array;
    }

    /** Returns the world elements with the largest ascender and descender */
    getTallestWordElements(): LargestWordElementsType {
        // get the word elements with the largest ascent
        let largestAscent = 0;
        let largestAscentText = '';

        // get the word elements with the largest descent
        let largestDescent = 0;
        let largestDescentText = '';

        this.lines.forEach((line) => {
            line.words.forEach((word) => {
                word.elements.forEach((element) => {
                    if (element.ascent > largestAscent) {
                        largestAscent = element.ascent;
                        largestAscentText = element.text;
                    }
                    if (element.descent > largestDescent) {
                        largestDescent = element.descent;
                        largestDescentText = element.text;
                    }
                });
            });
        });

        return {
            ascent: {
                text: largestAscentText,
                value: largestAscent,
            },
            descent: {
                text: largestDescentText,
                value: largestDescent,
            },
        };
    }

    /** Remove trailing punctuations from the last chunk on the last line. */
    removeTrailingPunctuations() {
        if (this.lines.length === 0) {
            return;
        }
        if (this.lines.at(-1)!.removeEndOfLinePunctuation()) {
            this.clearCachedValues();
        }
    }

    static getChunkTimeRanges(metrics: TextMetrics[], transcript: Transcript): number[][] {
        if (transcript.empty) {
            return [];
        }
        return metrics.map((chunkMetric) => {
            const [startIndex, endIndex] = chunkMetric.sourceWordReferenceIndexRange;
            return [transcript._words[startIndex!].startTime, transcript._words[endIndex!].endTime];
        });
    }

    static getChunkTexts(metrics: TextMetrics[]): string[] {
        return metrics.map((chunkMetric) => chunkMetric.text);
    }
}

/**
 * Sets the alignment and text direction of each line based on the alignment and the max line width.
 */
export function setAlignmentAndTextDirection(
    lines: TextLineMetrics[],
    alignment: TextAlignment,
    textDirection: TextDirection,
) {
    let maxLineWidth = 0;
    let minLineX = Infinity;
    lines.forEach((line) => {
        maxLineWidth = Math.max(maxLineWidth, line.bounds.width);
        minLineX = Math.min(minLineX, line.bounds.x);
    });
    lines.forEach((line) => {
        line.setAlignmentAndTextDirection(minLineX, maxLineWidth, alignment, textDirection);
    });
}

/**
 * Sets the position of the underlying word elements so that each line of words is positioned below the previous line
 * taking into account the line height.
 */
export function positionLinesBelowEachOther(lines: TextLineMetrics[], style: TextStyleRenderer) {
    // https://www.w3.org/TR/CSS22/visudet.html#line-height
    if (lines.length === 0) {
        return;
    }
    const leading = (style.baselineOffset - lines[0].height) * 0.5;
    let yBaseline = lines[0].ascent + leading;
    lines.forEach((line) => {
        line.setPosition(0, yBaseline);
        yBaseline += style.baselineOffset;
    });
}

function applyRangesToTokens(
    tokens: WordToken[],
    ranges: [number, number][],
    formatType: 'highlight' | 'bold' | 'italic' | 'underline',
) {
    ranges.forEach(([rangeStart, rangeEnd]) => {
        let charCounter = 0;

        tokens.forEach((token) => {
            const tokenStart = charCounter;
            const tokenEnd = tokenStart + token.length;
            charCounter += token.length;

            const hasOverlap = rangeStart < tokenEnd && rangeEnd > tokenStart;
            if (!hasOverlap) return;

            const startOffset = Math.max(0, rangeStart - tokenStart);
            const endOffset = Math.min(token.length, rangeEnd - tokenStart);

            if (formatType === 'highlight') {
                token.highlight(startOffset, endOffset);
                return;
            }

            token.applyFormat(formatType, startOffset, endOffset);
        });
    });
}

/**
 * Annotate words with additional information such as highlight status, formatting, and word reference indices.
 * If a word falls within the provided highlight areas then it will be marked as highlighted.
 * Formatting ranges (bold, italic, underline) are also applied to the appropriate word portions.
 * The word reference indices are used to map the word back to the original text.
 * If no indices are provided then the index of the word in the word list will be used.
 */
export function annotateWords(
    words: string[],
    highlightAreas: [number, number][],
    boldRanges: [number, number][] = [],
    italicRanges: [number, number][] = [],
    underlineRanges: [number, number][] = [],
    sourceWordReferenceIndices?: number[],
) {
    const annotatedWordTokens = words.map((word, index) => {
        const sourceWordReferenceIndex = sourceWordReferenceIndices
            ? sourceWordReferenceIndices[index]
            : index;
        return new WordToken(word, false, sourceWordReferenceIndex);
    });

    // Apply highlights first
    applyRangesToTokens(annotatedWordTokens, highlightAreas, 'highlight');

    // Apply formatting ranges
    applyRangesToTokens(annotatedWordTokens, boldRanges, 'bold');
    applyRangesToTokens(annotatedWordTokens, italicRanges, 'italic');
    applyRangesToTokens(annotatedWordTokens, underlineRanges, 'underline');

    return annotatedWordTokens;
}

/**
 * Creates the initial CanvasWordElementMetrics for each word token based on the assigned text style.
 */
export function createWordMetrics(
    wordTokens: WordToken[],
    normalStyle: TextStyleRenderer,
    highlightStyle: TextStyleRenderer,
    referenceToOutputMultiplier = 1,
) {
    const wordMetrics: WordMetrics[] = [];
    wordTokens.forEach((wordToken) => {
        const wordElementMetrics: WordElementMetrics[] = [];
        wordToken.wordElementTokens.forEach((wordElementToken) => {
            const style = getStyleForMetrics(
                wordElementToken.highlighted,
                normalStyle,
                highlightStyle,
            );
            const metrics = measureWord(
                wordElementToken.text,
                style,
                {
                    bold: wordElementToken.bold,
                    italic: wordElementToken.italic,
                },
                referenceToOutputMultiplier,
            );
            const wordElement = new WordElementMetrics(
                wordElementToken.text,
                normalStyle,
                highlightStyle,
                metrics,
                wordElementToken,
                referenceToOutputMultiplier,
            );
            if (isNewline(wordElementToken.text)) wordElement.hide();
            wordElementMetrics.push(wordElement);
        });
        wordMetrics.push(new WordMetrics(wordElementMetrics));
    });
    return wordMetrics;
}

/**
 * Check to see if any text words are too long for a single line and split as necessary. For example, the word
 * "pneumonoultramicroscopicsilicovolcanoconiosis" could get split over multiple lines as "pneumonoultramicroscopic" and
 * "silicovolcanoconiosis" hereby increasing the number of words. Note that we don't keep a reference to the original
 * words.
 */
export function fitWordsToWidth(
    InitWordMetrics: Array<WordMetrics>,
    wordBreak: boolean,
    maxWidth: number,
    normalStyle: TextStyleRenderer,
    highlightStyle: TextStyleRenderer,
    referenceToOutputMultiplier = 1,
): Array<WordMetrics> {
    if (!wordBreak) {
        return InitWordMetrics;
    }
    return InitWordMetrics.reduce((result, wordMetrics) => {
        const slice = (start: number, end: number) => {
            const slicedWordToken = wordMetrics.slice(start, end);
            const slicedTextWordMetrics = createWordMetrics(
                [slicedWordToken],
                normalStyle,
                highlightStyle,
                referenceToOutputMultiplier,
            );
            return slicedTextWordMetrics[0];
        };
        if (wordMetrics.advanceWidth >= maxWidth) {
            let currentStart = 0;
            for (let i = 1; i < wordMetrics.text.length; i++) {
                const thisMetrics = slice(currentStart, i + 1);
                if (thisMetrics.advanceWidth > maxWidth) {
                    result.push(slice(currentStart, i));
                    currentStart = i;
                }
            }
            if (currentStart < wordMetrics.text.length) {
                result.push(slice(currentStart, wordMetrics.text.length));
            }
        } else {
            result.push(wordMetrics);
        }
        return result;
    }, [] as WordMetrics[]);
}

/**
 * Assign text words to lines, if word wrap is enabled then fit text words based on the word wrap width
 */
export function assignWordsToLines(
    wordMetricsList: WordMetrics[],
    lineWidth: number,
): Array<TextLineMetrics> {
    const lines = [];
    let currentLine = new TextLineMetrics();
    wordMetricsList.forEach((wordMetrics) => {
        if (isNewline(wordMetrics.text)) {
            currentLine.add(wordMetrics);
            lines.push(currentLine);
            currentLine = new TextLineMetrics();
        } else {
            if (wordMetrics.advanceWidth + currentLine.advanceWidth > lineWidth) {
                lines.push(currentLine);
                currentLine = new TextLineMetrics();
            }
            currentLine.add(wordMetrics);
        }
    });
    if (currentLine.children.length > 0) {
        lines.push(currentLine);
    }
    return lines;
}

/**
 * Caps the number of lines based on the maxHeight.
 */
export function capLinesAtMaxHeight(textLineList: TextLineMetrics[], maxHeight: number) {
    if (textLineList.length === 0) {
        return [];
    }
    const offset = textLineList[0].ascentY;
    const index = textLineList.findIndex((line) => {
        const lineBottom = line.descentY - offset;
        return lineBottom > maxHeight && !equalWithinEpsilon(lineBottom, maxHeight);
    });
    return index === -1 ? textLineList : textLineList.splice(index, textLineList.length - index);
}

function scaleMetricsToOutputResolution(
    m: MeasurementResult,
    referenceToOutputMultiplier: number,
): MeasurementResult {
    return {
        width: m.width * referenceToOutputMultiplier,
        actualBoundingBoxAscent: m.actualBoundingBoxAscent * referenceToOutputMultiplier,
        actualBoundingBoxDescent: m.actualBoundingBoxDescent * referenceToOutputMultiplier,
        actualBoundingBoxLeft: m.actualBoundingBoxLeft * referenceToOutputMultiplier,
        actualBoundingBoxRight: m.actualBoundingBoxRight * referenceToOutputMultiplier,
        fontBoundingBoxAscent: m.fontBoundingBoxAscent * referenceToOutputMultiplier,
        fontBoundingBoxDescent: m.fontBoundingBoxDescent * referenceToOutputMultiplier,
    } as unknown as MeasurementResult;
}

/**
 * Return the TextMetrics of a word using the given font family and font size.
 */
export function measureWord(
    word: string,
    style: TextStyleRenderer,
    formatting: { bold?: boolean; italic?: boolean },
    referenceToOutputMultiplier = 1,
): MeasurementResult {
    const ctx = metricsContext();

    if (referenceToOutputMultiplier === 1) {
        style.setupContextWithStyle(ctx, 1, formatting);
        return ctx.measureText(word);
    }

    const originalFontSize = style.fontSize;
    style.fontSize /= referenceToOutputMultiplier;
    style.setupContextWithStyle(ctx, 1, formatting);
    style.fontSize = originalFontSize;
    return scaleMetricsToOutputResolution(ctx.measureText(word), referenceToOutputMultiplier);
}

/** Returns the strings with the largest ascender and descender from the provided array */
export function tallestStrings(array: LargestWordElementsType[]) {
    // get the word elements with the largest ascent
    let largestAscent = 0;
    let largestAscentText = '';

    // get the word elements with the largest descent
    let largestDescent = 0;
    let largestDescentText = '';

    array.forEach((element) => {
        if (element.ascent.value > largestAscent) {
            largestAscent = element.ascent.value;
            largestAscentText = element.ascent.text;
        }
        if (element.descent.value > largestDescent) {
            largestDescent = element.descent.value;
            largestDescentText = element.descent.text;
        }
    });

    const result = [largestAscentText];
    if (largestAscentText !== largestDescentText) {
        result.push(largestDescentText);
    }
    return result;
}

export function tallestStringsFromTranscript(transcript: Transcript, style: TextStyleRenderer) {
    if (transcript._words.length === 0) return [];

    const words: LargestWordElementsType[] = transcript._words.map((transcriptWordData) => {
        const { actualBoundingBoxAscent: ascent, actualBoundingBoxDescent: descent } = measureWord(
            transcriptWordData.word,
            style,
            {},
        );

        return {
            ascent: {
                text: transcriptWordData.word,
                value: ascent,
            },
            descent: {
                text: transcriptWordData.word,
                value: descent,
            },
        };
    });
    return tallestStrings(words);
}

/** Assign a globally unique index to each word. This allows us to figure out the position */
export function assignGloballyUniqueIndex(wordMetrics: WordMetrics[]) {
    let index = 0;
    wordMetrics.forEach((word) => {
        word.elements.forEach((element) => {
            element.setGlobalWordElementIndex(index);
            index++;
        });
    });
}

export const generateWordMetricsWhichFitWithinMaxWidth = ({
    words,
    highlightAreas = [],
    boldRanges = [],
    italicRanges = [],
    underlineRanges = [],
    sourceWordReferenceIndices,
    normalStyle,
    highlightStyle,
    maxWidth,
    wordBreak,
    referenceToOutputMultiplier = 1,
}: {
    words: string[];
    highlightAreas: Array<[number, number]>;
    boldRanges?: Array<[number, number]>;
    italicRanges?: Array<[number, number]>;
    underlineRanges?: Array<[number, number]>;
    sourceWordReferenceIndices: number[];
    normalStyle: TextStyleRenderer;
    highlightStyle: TextStyleRenderer;
    maxWidth: number;
    wordBreak: boolean;
    referenceToOutputMultiplier?: number;
}) => {
    const wordTokens = annotateWords(
        words,
        highlightAreas,
        boldRanges,
        italicRanges,
        underlineRanges,
        sourceWordReferenceIndices,
    );
    const wordMetrics = createWordMetrics(
        wordTokens,
        normalStyle,
        highlightStyle,
        referenceToOutputMultiplier,
    );
    return fitWordsToWidth(
        wordMetrics,
        wordBreak,
        maxWidth,
        normalStyle,
        highlightStyle,
        referenceToOutputMultiplier,
    );
};

/**
 * Splitting a word across a highlight boundary in RTL text breaks Canvas's per-call text shaping.
 * Two manifestations of the same root cause:
 *
 *   1. Number reordering: Canvas applies UAX-9 bidi per fillText call. Rule W4 only promotes a
 *      Common Separator like "." to a number when it has digits on both sides. Splitting "11.3"
 *      into "11" + ".3" strips the left-side digit from ".3" so W4 doesn't fire; the "." falls
 *      to paragraph direction and the substring renders as "3." Combined with "11" placed to its
 *      right in RTL flow, the user sees "3.11".
 *
 *   2. Arabic letter joining: Arabic glyphs take initial/medial/final/isolated forms based on
 *      their neighbors within a single shaping run. Splitting an Arabic word like "مليار" into
 *      "مل" + "يار" forces each piece to be shaped independently, so the letters at the split
 *      boundary lose their connecting forms and a visible gap appears.
 *
 * Both go away if a number, an Arabic word, or any other non-whitespace token stays inside one
 * WordTokenElement (and therefore one fillText call). Expand any highlight boundary that falls
 * inside a non-whitespace token outward to the nearest whitespace.
 */
export function expandHighlightsToWordBoundaries(
    text: string,
    highlightAreas: Array<[number, number]>,
): Array<[number, number]> {
    const splitsWord = (boundary: number) =>
        boundary > 0 &&
        boundary < text.length &&
        !isBreakingSpace(text[boundary - 1]) &&
        !isNewline(text[boundary - 1]) &&
        !isBreakingSpace(text[boundary]) &&
        !isNewline(text[boundary]);

    return highlightAreas.map(([start, end]) => {
        let newStart = start;
        let newEnd = end;
        while (splitsWord(newStart)) newStart--;
        while (splitsWord(newEnd)) newEnd++;
        return [newStart, newEnd];
    });
}

/**
 * Builds the metrics for a block of text. The text will be broken down into lines and words. Each word will be broken
 * down into word elements.
 * @param wordBreak - if true, the text will be wrapped to the maxWidth, if false, the text will be truncated at the maxWidth.
 * We should use true when rendering text and false when sizing or fitting text for the best visual results.
 */
export const buildMetricsForText = ({
    text,
    alignment = TextAlignment.LEFT,
    normalStyle,
    highlightStyle,
    highlightAreas = [],
    boldRanges = [],
    italicRanges = [],
    underlineRanges = [],
    wordBreak = false,
    maxWidth = Infinity,
    maxHeight = Infinity,
    referenceToOutputMultiplier = 1,
}: {
    text: string;
    alignment?: TextAlignment;
    normalStyle: TextStyleRenderer;
    highlightStyle: TextStyleRenderer;
    highlightAreas?: Array<[number, number]>;
    boldRanges?: Array<[number, number]>;
    italicRanges?: Array<[number, number]>;
    underlineRanges?: Array<[number, number]>;
    wordBreak?: boolean;
    maxWidth?: number;
    maxHeight?: number;
    referenceToOutputMultiplier?: number;
}) => {
    const words = tokenize(text);

    const effectiveHighlightAreas =
        normalStyle.textDirection === TextDirection.RTL
            ? expandHighlightsToWordBoundaries(text, highlightAreas)
            : highlightAreas;

    const wordMetrics = generateWordMetricsWhichFitWithinMaxWidth({
        words,
        highlightAreas: effectiveHighlightAreas,
        boldRanges,
        italicRanges,
        underlineRanges,
        sourceWordReferenceIndices: [],
        normalStyle,
        highlightStyle,
        wordBreak,
        maxWidth,
        referenceToOutputMultiplier,
    });

    assignGloballyUniqueIndex(wordMetrics);

    const lineMetrics = assignWordsToLines(wordMetrics, maxWidth);
    const textDirection = normalStyle.textDirection;
    lineMetrics.forEach((line) => line.removeLeadingTrailingSpaces(alignment, textDirection));
    const lineMetricsWithContent = lineMetrics.filter((line) => line.text.length > 0);
    positionLinesBelowEachOther(lineMetricsWithContent, normalStyle);
    setAlignmentAndTextDirection(lineMetricsWithContent, alignment, textDirection);
    const linesBefore = lineMetrics.length;
    capLinesAtMaxHeight(lineMetricsWithContent, maxHeight);

    let canDisplayAllText = linesBefore === lineMetricsWithContent.length;
    if (canDisplayAllText && !wordBreak) {
        // With wordBreak disabled, an oversized word is laid out on its own line rather
        // than being split — it still overflows the box horizontally. Treat that as not
        // fitting so a font-size fit search can pick a smaller size.
        canDisplayAllText = lineMetricsWithContent.every((line) => line.advanceWidth <= maxWidth);
    }

    return new TextMetrics(lineMetricsWithContent, canDisplayAllText);
};
